import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Clock,
  Users,
  Zap,
  MessageSquare,
  Phone,
  Mail,
} from "lucide-react";

const CustomerService = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Clock,
      title: t("customerService.features.support.title"),
      description: t("customerService.features.support.description"),
    },
    {
      icon: Users,
      title: t("customerService.features.dedicated.title"),
      description: t("customerService.features.dedicated.description"),
    },
    {
      icon: Zap,
      title: t("customerService.features.response.title"),
      description: t("customerService.features.response.description"),
    },
    {
      icon: MessageSquare,
      title: t("customerService.features.communication.title"),
      description: t("customerService.features.communication.description"),
    },
  ];

  return (
    <div className="min-h-screen bg-accent-50">
      {/* Hero Section */}
      <section className="relative text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/images/customer_service.webp"
            alt="Modern office space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-green-800/90"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t("customerService.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-accent-100">
              {t("customerService.hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="text-green-700 mb-8">
              {t("customerService.main.intro")}
            </p>
            <p className="text-green-700 mb-8">
              {t("customerService.main.response")}
            </p>
            <p className="text-green-700 mb-8">
              {t("customerService.main.commitment")}
            </p>
            <p className="text-green-700 mb-8">
              {t("customerService.main.directCommunication")}
            </p>
            <p className="text-green-700 mb-8">
              {t("customerService.main.valueStatement")}
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border-l-4 border-transparent hover:border-orange-500"
              >
                <feature.icon className="w-12 h-12 text-orange-500 mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-green-700">{feature.title}</h3>
                <p className="text-green-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              {t("customerService.cta.title")}
            </h2>
            <p className="text-xl text-accent-300 mb-8">
              {t("customerService.cta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600"
              >
                <Mail className="w-5 h-5 mr-2" />
                {t("customerService.cta.contact")}
              </Link>
              <a
                href="tel:+1234567890"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-green-700 bg-white hover:bg-green-50"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t("customerService.cta.phone")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerService;